// Full copies of Inventory and My eBay listings kept on the phone; searching, filtering and sorting run here, not on the server.
import { api } from "@/lib/api";
import { copyStale as stale, readCache, remoteChange, writeCache } from "@/lib/cache";
import { readSpecFilter, specHas } from "@/lib/specs";

const INV = "local:inventory", LST = "local:ebay-listings";
const FULL_EVERY = 24 * 3600e3;

export const cachedInventory = async () => (await readCache(INV, true))?.data || null;
export const cachedListings = async () => (await readCache(LST, true))?.data || null;

// Only what changed since the last sync comes down (a full copy every 6 hours or on Refresh).
export async function syncInventory(force = false) {
  const st = await cachedInventory();
  if (!force && !stale(st)) return st;
  const full = force || !st || Date.now() - (st.fullAt || 0) > FULL_EVERY;
  try {
    const { data } = await api.get("/inventory/all", { params: full ? {} : { since: st.synced_at }, fresh: true, timeout: 90000 });
    let rows = data.items;
    if (!data.full) {
      const keep = new Set(data.ids);
      const m = new Map(st.rows.filter((r) => keep.has(r.id)).map((r) => [r.id, r]));
      data.items.forEach((r) => m.set(r.id, r));
      rows = [...m.values()];
    }
    const next = { rows, synced_at: data.synced_at, fullAt: full ? Date.now() : st.fullAt, localAt: Date.now(), seen: remoteChange() };
    writeCache(INV, next);
    return next;
  } catch (e) {
    if (st) return st;
    throw e;
  }
}

export async function syncListings(force = false) {
  const st = await cachedListings();
  if (!force && !stale(st)) return st;
  try {
    const { data } = await api.get("/ebay/my-listings/all", { params: st && !force ? { have: st.version } : {}, fresh: true, timeout: 90000 });
    const next = data.unchanged ? { ...st, localAt: Date.now(), seen: remoteChange() } : { items: data.items, version: data.version, refreshed_at: data.refreshed_at, localAt: Date.now(), seen: remoteChange() };
    writeCache(LST, next);
    return next;
  } catch (e) {
    if (st) return st;
    throw e;
  }
}

const daysSince = (iso) => { const t = Date.parse(iso || ""); return Number.isFinite(t) ? Math.max(0, Math.floor((Date.now() - t) / 864e5)) : 0; };
const binCode = (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, "-").slice(0, 24);

export function filterInventory(rows, f) {
  const q = (f.q || "").trim().toLowerCase();
  const want = readSpecFilter(f.spec);
  const bin = f.bin === "_none" ? "_none" : binCode(f.bin);
  const aged = Number(f.aged) || 0;
  let out = (rows || []).map((r) => ({ ...r, days: daysSince(r.created_at) })).filter((r) => r.quantity > 0);
  if (f.bin) out = out.filter((r) => (bin === "_none" ? !r.bin : binCode(r.bin) === bin));
  if (q) {
    out = out.filter((r) => [r.title, r.sku, r.bin, r.barcode, ...(r.alt_skus || [])].some((v) => String(v || "").toLowerCase().includes(q))
      || (q.length >= 3 && r.id.toLowerCase().startsWith(q)));
  }
  if (want) out = out.filter((r) => specHas(`${r.title} ${r.spec_text || ""}`, want));
  if (aged) out = out.filter((r) => r.days >= aged);
  const by = {
    value: (a, b) => b.cost * b.quantity - a.cost * a.quantity,
    bin: (a, b) => (a.bin || "~").localeCompare(b.bin || "~") || a.title.localeCompare(b.title),
    title: (a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
  }[f.sort] || ((a, b) => b.days - a.days);
  out.sort(by);
  if (q) out.sort((a, b) => Number((b.sku || "").toLowerCase() === q) - Number((a.sku || "").toLowerCase() === q));
  const summary = {
    items: out.length, units: out.reduce((s, r) => s + r.quantity, 0),
    cost: Math.round(out.reduce((s, r) => s + r.cost * r.quantity, 0) * 100) / 100,
    retail: Math.round(out.reduce((s, r) => s + r.price * r.quantity, 0) * 100) / 100,
    avg_days: out.length ? Math.round(out.reduce((s, r) => s + r.days, 0) / out.length) : 0,
    aged_90: out.filter((r) => r.days >= 90).length, no_bin: out.filter((r) => !r.bin).length,
  };
  return { items: out, summary };
}

const ORDER = {
  newest: (a, b) => String(b.listed_at || "").localeCompare(String(a.listed_at || "")),
  oldest: (a, b) => String(a.listed_at || "").localeCompare(String(b.listed_at || "")),
  price_high: (a, b) => (b.price || 0) - (a.price || 0),
  price_low: (a, b) => (a.price || 0) - (b.price || 0),
  watchers: (a, b) => (b.watchers || 0) - (a.watchers || 0) || String(b.listed_at || "").localeCompare(String(a.listed_at || "")),
};

export function filterListings(items, { q, from, to, sort }) {
  const s = (q || "").trim().toLowerCase();
  const out = (items || []).filter((it) => (!s || (it.title || "").toLowerCase().includes(s) || (it.sku || "").toLowerCase().includes(s) || it.item_id === s)
    && (!from || (it.listed_at || "") >= from) && (!to || (it.listed_at || "") < to));
  return out.sort(ORDER[sort] || ORDER.newest);
}
